import { useEffect, useState, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, WMSTileLayer, CircleMarker, Popup } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, AlertTriangle, Home, Maximize2, Minimize2, Search, Database, Wifi, ScanLine, Palette } from 'lucide-react';
import { ProgressPanel } from './ProgressPanel';
import { Button } from './ui/button';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import 'leaflet/dist/leaflet.css';
import InvestigationPanel from './InvestigationPanel';
import HeatMapLayer from './HeatMapLayer';
import { FitBounds, InvalidateSize, ZoomTracker } from './map/MapHelpers';
import AddressModal from './map/AddressModal';
import { MapControls } from './map/MapControls';
import { GeocodingStatus } from './map/GeocodingStatus';
import { HeatMapLegend } from './map/HeatMapLegend';
import { useMapData } from '../hooks/useMapData';
import { useInvestigation } from '../hooks/useInvestigation';
import { useMapControls } from '../hooks/useMapControls';
import { useAuth } from '../context/AuthContext';
import { authHeaders } from '../utils/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

export default function DonorMap({ datasetIds }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [modalAddress, setModalAddress] = useState(null);
  const [modalDonor, setModalDonor] = useState(null);
  const [heatStats, setHeatStats] = useState(null);

  const controls = useMapControls();
  const mapData = useMapData(datasetIds, controls.selectedYears);
  const investigation = useInvestigation(datasetIds);

  // Fetch available years when datasets change
  useEffect(() => {
    if (!datasetIds) {
      controls.setAvailableYears([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/map/available-years?dataset_ids=${datasetIds}`, { headers: authHeaders() });
        const data = await res.json();
        controls.setAvailableYears(data.years || []);
      } catch (e) {
        console.error('Failed to fetch available years:', e);
      }
    })();
  }, [datasetIds]);

  const {
    locations, fallbackLocations, confirmedCount, approximateCount,
    geocodingPending, geocodingActive, geocodingSticky, totalAddresses,
    initialLoading, refreshing, mapCenter, fitKey,
    fetchLocations, fetchGeocodingStatus, handleRefresh,
  } = mapData;

  const {
    showApproximate, isFullscreen, setIsFullscreen,
    showZipBoundaries, colorMode, pinSize, mapRef,
    visibleCategories, isFiltered, resetFilters,
    getCategoryKey, getMarkerColor, setMapZoom,
  } = controls;

  // Fast color + visibility update: bypass React, update Leaflet layers directly
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.eachLayer((layer) => {
      if (!layer._locationData) return;
      const loc = layer._locationData;
      const catKey = getCategoryKey(loc);
      const visible = visibleCategories[catKey];
      const newColor = getMarkerColor(loc);
      const isApprox = loc.is_approximate;
      if (visible) {
        layer.setStyle({
          fillColor: newColor,
          color: newColor,
          opacity: isApprox ? 0.4 : 0.8,
          fillOpacity: isApprox ? 0.25 : 0.6,
        });
        layer.options.interactive = true;
      } else {
        layer.setStyle({ opacity: 0, fillOpacity: 0 });
        layer.options.interactive = false;
      }
    });
  }, [colorMode, visibleCategories, getMarkerColor, getCategoryKey]);

  // Auto-refresh polling — stays alive while geocodingSticky is true (grace period)
  useEffect(() => {
    if (initialLoading) return;
    const shouldPoll = geocodingActive || geocodingPending > 0 || geocodingSticky;
    if (!shouldPoll) return;
    let tick = 0;
    let fetchInProgress = false;
    const interval = setInterval(async () => {
      tick++;
      fetchGeocodingStatus();
      const reloadEvery = isFullscreen ? 3 : 6;
      if (tick % reloadEvery === 0 && !fetchInProgress) {
        fetchInProgress = true;
        await fetchLocations(true, 0, true);
        fetchInProgress = false;
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [initialLoading, geocodingPending, geocodingActive, geocodingSticky, isFullscreen, fetchLocations, fetchGeocodingStatus]);

  // Lock page scroll + hide sidebar while fullscreen is active so the map
  // overlay really fills the viewport with no scroll bars underneath.
  useEffect(() => {
    if (!isFullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isFullscreen]);

  const allLocations = showApproximate ? [...locations, ...fallbackLocations] : locations;
  const boundsLocations = [...locations, ...fallbackLocations]; // Always include all for zoom fitting
  const filteredLocations = allLocations.filter(l => visibleCategories[getCategoryKey(l)]);
  const multiDonorCount = allLocations.filter(l => l.is_multi_donor).length;
  const highActivityCount = allLocations.filter(l => l.is_multi_donor && l.unique_donors >= controls.multiThreshold).length;

  // Locally-tagged cluster keys ("street|zip") removed from the navigator
  // immediately so the counter decrements even for Skip (which doesn't alter
  // map-side party classification). On re-fetch, Rep/Dem tags naturally drop
  // the cluster from allLocations with party!=unknown; Skip stays in the local
  // set for the life of this page view.
  const [locallyTagged, setLocallyTagged] = useState(() => new Set());

  // Sorted list of unknown clusters (by donation count desc) so clicking a
  // gray pin lets the user scroll Prev/Next through every unknown to tag it.
  const unknownList = allLocations
    .filter(l => (l.party || 'unknown') === 'unknown' && !l.is_approximate)
    .filter(l => !locallyTagged.has(`${l.street}|${l.zip}`))
    .sort((a, b) => (b.donation_count || 0) - (a.donation_count || 0));
  const unknownIndex = modalAddress
    ? unknownList.findIndex(l => l.street === modalAddress.street && l.zip === modalAddress.zip)
    : -1;
  const handleNavigate = (newIdx) => {
    const target = unknownList[newIdx];
    if (target) { setModalAddress(target); setModalDonor(null); }
  };
  const canTag = user?.role === 'superadmin';

  // Clear heat stats when investigation is not active or not in heat mode
  const isHeatMode = investigation.isActive && (investigation.mode === 'heat_dollars' || investigation.mode === 'heat_count');
  useEffect(() => {
    if (!isHeatMode) setHeatStats(null);
  }, [isHeatMode]);

  return (
    <>
      {modalAddress && (
        <AddressModal
          address={modalAddress} datasetIds={datasetIds} donorFilter={modalDonor}
          onClose={() => { setModalAddress(null); setModalDonor(null); }}
          unknownList={unknownIndex >= 0 ? unknownList : null}
          unknownIndex={unknownIndex >= 0 ? unknownIndex : null}
          onNavigate={unknownIndex >= 0 ? handleNavigate : null}
          canTag={canTag}
          onClusterTagged={(addr) => {
            setLocallyTagged((prev) => {
              const next = new Set(prev);
              next.add(`${addr.street}|${addr.zip}`);
              return next;
            });
          }}
        />
      )}
      {investigation.showInvestigation && (
        <InvestigationPanel
          datasetIds={datasetIds}
          onApply={investigation.applyInvestigationFilters}
          onClose={() => investigation.setShowInvestigation(false)}
          activeFilters={investigation.investigationFilters}
        />
      )}
      <Card
        className={`border rounded-sm shadow-sm transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-[2000] rounded-none flex flex-col overflow-hidden' : ''}`}
        data-testid="donor-map"
      >
        <CardHeader className={`bg-card border-b ${isFullscreen ? 'flex-shrink-0 max-h-[55vh] overflow-y-auto !p-0' : 'sticky top-0 z-[500] pb-0'}`}>
          <div className={`flex items-center justify-between gap-2 flex-wrap ${isFullscreen ? 'px-3 py-1.5' : 'pb-2'}`}>
            <CardTitle className={`font-heading font-medium flex items-center gap-2 ${isFullscreen ? 'text-sm' : 'text-base md:text-lg'}`}>
              <MapPin className="w-4 h-4" /> Donor Address Map
            </CardTitle>
            <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
              {highActivityCount > 0 && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] md:text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" /> {highActivityCount} High
                </Badge>
              )}
              {multiDonorCount > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] md:text-xs hidden sm:inline-flex">
                  <Home className="w-3 h-3 mr-1" /> {multiDonorCount} Multi-Donor
                </Badge>
              )}
              <button
                onClick={() => investigation.setShowInvestigation(true)}
                className={`ml-1 px-2.5 py-1 rounded-md border text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  investigation.isActive ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted border-border'
                }`}
                data-testid="investigation-btn" title="Investigation Refinement"
              >
                <Search className="w-3.5 h-3.5" /> Investigate
              </button>
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="ml-2 p-1.5 rounded-md border hover:bg-muted transition-colors"
                data-testid="map-fullscreen-btn" title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <MapControls {...controls} compact={isFullscreen} />

          <GeocodingStatus
            totalAddresses={totalAddresses} geocodingPending={geocodingPending}
            geocodingActive={geocodingActive} geocodingSticky={geocodingSticky}
            confirmedCount={confirmedCount}
            approximateCount={approximateCount} showApproximate={showApproximate}
            setShowApproximate={controls.setShowApproximate}
            isFiltered={isFiltered} filteredCount={filteredLocations.length}
            totalCount={locations.length} refreshing={refreshing}
            handleRefresh={handleRefresh}
            compact={isFullscreen}
          />
        </CardHeader>
        <CardContent className={`p-0 relative ${isFullscreen ? '!p-0 flex-1 min-h-0 flex flex-col' : ''}`}>
          {refreshing && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-2 bg-card/95 backdrop-blur-sm px-3 py-1.5 rounded-sm shadow-sm border text-xs text-muted-foreground" data-testid="map-refreshing">
              <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              Updating map...
            </div>
          )}

          {isFiltered && (
            <div className="mx-4 mb-1 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs flex items-center justify-between">
              <span className="text-amber-800">Filtered view — showing {filteredLocations.length.toLocaleString()} of {locations.length.toLocaleString()} addresses</span>
              <button onClick={resetFilters} className="text-amber-700 font-medium hover:text-amber-900 underline">Clear Filters</button>
            </div>
          )}

          {investigation.isActive && (
            <div className="mx-4 mb-1 px-3 py-1.5 bg-primary/5 border border-primary/20 rounded text-xs flex items-center justify-between" data-testid="investigation-bar">
              <span className="text-primary font-medium flex items-center gap-1.5">
                <Search className="w-3 h-3" />
                {investigation.investigationLoading ? 'Searching...' : (
                  investigation.investigationData ? (
                    <>
                      Investigation: {investigation.investigationData.total_matched_addresses?.toLocaleString()} addresses, {investigation.investigationData.total_matched_donors?.toLocaleString()} donors
                      {investigation.mode !== 'pins' && <span className="ml-1.5 text-muted-foreground">({investigation.mode === 'heat_dollars' ? '$ Heat Map' : '# Heat Map'})</span>}
                    </>
                  ) : 'Applying filters...'
                )}
              </span>
              <button onClick={investigation.clearInvestigation} className="text-primary font-medium hover:underline">Clear Investigation</button>
            </div>
          )}

          <div className={isFullscreen ? 'flex-1 min-h-0' : 'h-[calc(100vh-180px)]'}>
            {!datasetIds ? (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MapPin className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Select a dataset to view the map</p>
                </div>
              </div>
            ) : (
              <div className="relative h-full">
                {/* Loading overlay - shown while pins are loading */}
                {initialLoading && locations.length === 0 && (
                  <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-background/95 backdrop-blur-sm">
                    <ProgressPanel
                      variant="overlay"
                      contextLabel="Building map clusters"
                      phases={[
                        { icon: Database,  label: "Reading donations from database", min: 0 },
                        { icon: Wifi,      label: "Merging address clusters",        min: 4 },
                        { icon: ScanLine,  label: "Matching to geocoded coordinates", min: 9 },
                        { icon: Palette,   label: "Painting pins on the map",        min: 14 },
                      ]}
                      reassurances={{
                        5: "First load of a large folder can take up to 30 seconds — cached instantly after.",
                        15: "Aggregating millions of donations on the shared MongoDB cluster. Almost there…",
                        30: "Still working. This is a very large selection — result will be cached so this is a one-time wait.",
                      }}
                      onRetry={handleRefresh}
                      retryLabel={refreshing ? 'Loading…' : 'Retry Now'}
                    />
                  </div>
                )}
                <MapContainer
                  center={mapCenter ? [mapCenter.lat, mapCenter.lng] : [39.8283, -98.5795]}
                  zoom={mapCenter ? 13 : 4} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}
                >
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {showZipBoundaries && (
                  <WMSTileLayer
                    url="https://tigerweb.geo.census.gov/arcgis/services/TIGERweb/tigerWMS_Current/MapServer/WMSServer"
                    layers="76,77" format="image/png" transparent={true} opacity={0.6} version="1.1.1"
                    attribution='&copy; US Census Bureau'
                  />
                )}
                <FitBounds locations={boundsLocations} fitKey={fitKey} center={mapCenter} />
                <InvalidateSize trigger={isFullscreen} />
                <ZoomTracker onZoomChange={setMapZoom} mapRefCb={(m) => { mapRef.current = m; }} />

                {/* Investigation heat map */}
                {investigation.isActive && investigation.investigationData && (investigation.mode === 'heat_dollars' || investigation.mode === 'heat_count') && investigation.investigationData.heat_data?.length > 0 && (
                  <HeatMapLayer
                    points={investigation.investigationData.heat_data}
                    options={{ radius: 25, blur: 15, maxZoom: 18, minOpacity: 0.35 }}
                    onStatsReady={setHeatStats}
                  />
                )}

                {/* Markers */}
                {investigation.isActive && investigation.investigationData && investigation.mode === 'pins' ? (
                  investigation.investigationData.locations?.map((location, index) => (
                    <MarkerPin key={`inv-${index}`} location={location} pinSize={pinSize} getMarkerColor={getMarkerColor}
                      navigate={navigate} setModalAddress={setModalAddress} formatCurrency={formatCurrency} isInvestigation />
                  ))
                ) : !investigation.isActive && (
                  allLocations.map((location, index) => (
                    <MarkerPin key={`${location.lat}-${location.lng}-${index}`} location={location} pinSize={pinSize} getMarkerColor={getMarkerColor}
                      navigate={navigate} setModalAddress={setModalAddress} formatCurrency={formatCurrency} />
                  ))
                )}
              </MapContainer>
              {/* Heat map legend overlay */}
              {investigation.isActive && investigation.investigationData && (investigation.mode === 'heat_dollars' || investigation.mode === 'heat_count') && heatStats && (
                <HeatMapLegend stats={heatStats} mode={investigation.mode} />
              )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

const MarkerPin = memo(function MarkerPin({ location, pinSize, getMarkerColor, navigate, setModalAddress, formatCurrency, isInvestigation }) {
  const isApprox = location.is_approximate;
  const markerColor = getMarkerColor(location);

  return (
    <CircleMarker
      center={[location.lat, location.lng]}
      radius={pinSize}
      pathOptions={{
        fillColor: markerColor,
        color: markerColor,
        weight: isApprox ? 1 : 2,
        opacity: isApprox ? 0.4 : 0.8,
        fillOpacity: isApprox ? 0.25 : 0.6,
        dashArray: isApprox ? '4 3' : undefined,
      }}
      eventHandlers={{ add: (e) => { e.target._baseR = pinSize; e.target._locationData = location; } }}
    >
      <Popup>
        <div className="min-w-[220px]">
          {isApprox && (
            <div className="mb-2 px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs text-gray-600 font-medium">
              Approximate location (ZIP code center)
            </div>
          )}
          <h3 className="font-semibold text-sm mb-1">{location.address}</h3>
          {location.is_multi_donor && !isInvestigation && (
            <div className="mb-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 font-medium">
              {location.unique_donors} different donors at this address
            </div>
          )}
          <div className="space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-gray-600">{isInvestigation ? 'Donations' : 'Total Donations'}:</span><span className="font-mono font-medium">{location.donation_count}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">{isInvestigation ? 'Donors' : 'Unique Donors'}:</span><span className="font-mono font-medium">{location.unique_donors}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Total:</span><span className="font-mono font-medium text-emerald-600">{formatCurrency(location.total_amount)}</span></div>
            {isInvestigation && location.years_active && (
              <div className="flex justify-between"><span className="text-gray-600">Years Active:</span><span className="font-mono font-medium">{location.years_active} ({location.years?.join(', ')})</span></div>
            )}
          </div>
          {location.top_donors && location.top_donors.length > 0 && (
            <div className="mt-2 pt-2 border-t">
              <p className="text-xs font-medium mb-1">{isInvestigation ? 'Matching Donors' : 'Donors'}:</p>
              {location.top_donors.slice(0, 5).map((donor, i) => (
                <div key={i} className="text-xs flex justify-between gap-2">
                  <button
                    className="truncate max-w-[130px] text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                    data-testid={`donor-link-${i}`}
                    onClick={(e) => { e.stopPropagation(); navigate(`/data?donor=${encodeURIComponent(donor.name)}`); }}
                  >
                    {donor.name}
                  </button>
                  <span className="font-mono whitespace-nowrap">
                    {formatCurrency(donor.amount)}
                    {donor.count > 1 && <span className="text-gray-400 ml-1">({donor.count}x)</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
          {!isInvestigation && location.by_year && location.by_year.length > 1 && (
            <div className="mt-2 pt-2 border-t">
              <p className="text-xs font-medium mb-1">Donations by Year</p>
              <div style={{ width: '100%', height: 100 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={location.by_year} margin={{ top: 2, right: 0, left: -20, bottom: 0 }}>
                    <XAxis dataKey="year" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="left" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                    <Tooltip
                      formatter={(v, name) => [name === 'amount' ? formatCurrency(v) : v, name === 'amount' ? 'Amount' : '# Donations']}
                      contentStyle={{ fontSize: '10px', padding: '4px 8px' }}
                    />
                    <Bar yAxisId="left" dataKey="amount" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="count" stroke="#f97316" strokeWidth={2} dot={{ r: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {location.street && (
            <button
              className="mt-2 w-full text-xs bg-slate-900 text-white py-1.5 px-2 rounded hover:bg-slate-700 transition-colors font-medium"
              data-testid="view-address-data-btn"
              onClick={() => setModalAddress(location)}
            >
              View All Data
            </button>
          )}
        </div>
      </Popup>
    </CircleMarker>
  );
}, (prev, next) => {
  // Only re-render if location identity changed (new data). Color, visibility, and size changes are handled by direct Leaflet updates.
  return prev.location === next.location;
});
